import { type FormEvent, type ReactNode, useId } from "react";

import { ArrowClockwiseIcon } from "@phosphor-icons/react";
import {
  Button,
  Flex,
  Popover,
  SegmentedControl,
  Select,
  Text,
  TextField,
} from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";
import { formatLastUpdatedAt } from "~/v3-poc/utils/lastUpdated";
import type {
  OrdersWidgetFilters,
  OrdersWidgetStatusTab,
  OrdersWidgetTimeWindow,
} from "./OrdersWidget.types";
import {
  formatOrdersTimeWindowShort,
  getSelectedStatusTab,
  isOrdersStatusTab,
} from "./OrdersWidget.utils";

interface FilterOption<TValue extends string> {
  label: string;
  value: TValue;
}

const STATUS_TAB_OPTIONS: FilterOption<OrdersWidgetStatusTab>[] = [
  { label: "All", value: "all" },
  { label: "Open", value: "open" },
  { label: "Filled", value: "filled" },
  { label: "Closed", value: "closed" },
];

const DIRECTION_OPTIONS: FilterOption<OrdersWidgetFilters["direction"]>[] = [
  { label: "Newest first", value: "desc" },
  { label: "Oldest first", value: "asc" },
];

const TIME_WINDOW_OPTIONS: FilterOption<OrdersWidgetTimeWindow>[] = [
  { label: "All", value: "all" },
  { label: "Last 24 hours", value: "last24Hours" },
  { label: "Last 3 days", value: "last3Days" },
  { label: "Last 7 days", value: "last7Days" },
  { label: "Last 1 month", value: "last1Month" },
  { label: "Custom range", value: "custom" },
];

const TIME_WINDOW_SHORT_BUTTON_LABEL = (
  timeWindow: OrdersWidgetTimeWindow,
): string => `Time · ${formatOrdersTimeWindowShort(timeWindow)}`;

const getFilterValue = <TValue extends string>(
  options: FilterOption<TValue>[],
  value: string,
): TValue | undefined =>
  options.find((option) => option.value === value)?.value;

const updateFilterValue = <TValue extends string>(
  options: FilterOption<TValue>[],
  value: string,
  onChange: (value: TValue) => void,
) => {
  const filterValue = getFilterValue(options, value);
  if (filterValue) {
    onChange(filterValue);
  }
};

interface OrdersTimeWindowButtonProps {
  filters: OrdersWidgetFilters;
  formId: string;
  validationError?: string;
  validationErrorId: string;
  onFiltersChange: (filters: OrdersWidgetFilters) => void;
}

const OrdersTimeWindowButton = ({
  filters,
  formId,
  validationError,
  validationErrorId,
  onFiltersChange,
}: OrdersTimeWindowButtonProps) => (
  <Popover.Root>
    <Popover.Trigger>
      <Button type="button" size="1" variant="soft" color="gray">
        {TIME_WINDOW_SHORT_BUTTON_LABEL(filters.timeWindow)}
      </Button>
    </Popover.Trigger>
    <Popover.Content width="280px" maxWidth="calc(100vw - 2rem)">
      <Flex direction="column" gap="3">
        <Text size="2" weight="bold">
          Submitted time
        </Text>
        <Select.Root
          size="1"
          value={filters.timeWindow}
          onValueChange={(value) =>
            updateFilterValue(TIME_WINDOW_OPTIONS, value, (timeWindow) =>
              onFiltersChange({
                ...filters,
                timeWindow,
                ...(timeWindow === "custom"
                  ? {}
                  : { fromDate: "", toDate: "" }),
              }),
            )
          }
        >
          <Select.Trigger aria-label="Order time window" />
          <Select.Content>
            {TIME_WINDOW_OPTIONS.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        {filters.timeWindow === "custom" ? (
          <Flex gap="2">
            <TextField.Root
              form={formId}
              size="1"
              type="date"
              value={filters.fromDate}
              max={filters.toDate || undefined}
              aria-label="Orders from date"
              aria-describedby={validationError ? validationErrorId : undefined}
              aria-invalid={validationError ? true : undefined}
              onChange={(event) =>
                onFiltersChange({
                  ...filters,
                  fromDate: event.currentTarget.value,
                })
              }
            />
            <TextField.Root
              form={formId}
              size="1"
              type="date"
              value={filters.toDate}
              min={filters.fromDate || undefined}
              aria-label="Orders to date"
              aria-describedby={validationError ? validationErrorId : undefined}
              aria-invalid={validationError ? true : undefined}
              onChange={(event) =>
                onFiltersChange({
                  ...filters,
                  toDate: event.currentTarget.value,
                })
              }
            />
          </Flex>
        ) : null}
        <Select.Root
          size="1"
          value={filters.direction}
          onValueChange={(value) =>
            updateFilterValue(DIRECTION_OPTIONS, value, (direction) =>
              onFiltersChange({ ...filters, direction }),
            )
          }
        >
          <Select.Trigger aria-label="Submitted time sort direction" />
          <Select.Content>
            {DIRECTION_OPTIONS.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Flex>
    </Popover.Content>
  </Popover.Root>
);

const OrdersStatusTabs = ({
  selectedStatusTab,
  rowCount,
  onStatusTabChange,
}: {
  selectedStatusTab: OrdersWidgetStatusTab;
  rowCount: number;
  onStatusTabChange: (value: OrdersWidgetStatusTab) => void;
}) => (
  <SegmentedControl.Root
    size="1"
    value={selectedStatusTab}
    aria-label="Order status"
    onValueChange={(value) => {
      if (isOrdersStatusTab(value) && value !== selectedStatusTab) {
        onStatusTabChange(value);
      }
    }}
  >
    {STATUS_TAB_OPTIONS.map((option) => {
      const showCount = option.value === selectedStatusTab;
      return (
        <SegmentedControl.Item key={option.value} value={option.value}>
          {showCount ? `${option.label} (${rowCount})` : option.label}
        </SegmentedControl.Item>
      );
    })}
  </SegmentedControl.Root>
);
const OrdersValidationNotice = ({
  validationError,
  validationErrorId,
}: {
  validationError?: string;
  validationErrorId: string;
}) => {
  if (!validationError) {
    return null;
  }

  return (
    <Text id={validationErrorId} size="1" color="red" role="alert" mt="2">
      {validationError}
    </Text>
  );
};

export interface OrdersFiltersPresentationProps {
  surface: "standalone" | "workspace";
  filters: OrdersWidgetFilters;
  formKey: number;
  validationError?: string;
  onFiltersChange: (filters: OrdersWidgetFilters) => void;
  onStatusTabChange: (status: OrdersWidgetStatusTab) => void;
  onReset: () => void;
  rowCount: number;
  dataUpdatedAt?: number;
  symbolSearch: ReactNode | null;
  columnsPopover: ReactNode;
  onRefresh: () => void;
  isRefreshDisabled?: boolean;
  isRefreshing?: boolean;
}

export const OrdersFiltersPresentation = ({
  surface,
  filters,
  formKey,
  validationError,
  onFiltersChange,
  onStatusTabChange,
  onReset,
  rowCount,
  dataUpdatedAt,
  symbolSearch,
  columnsPopover,
  onRefresh,
  isRefreshDisabled = false,
  isRefreshing = false,
}: OrdersFiltersPresentationProps) => {
  const formId = useId();
  const validationErrorId = useId();
  const lastUpdatedLabel = formatLastUpdatedAt(dataUpdatedAt);
  const isWorkspaceSurface = surface === "workspace";
  const selectedStatusTab = getSelectedStatusTab(filters.status);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  return (
    <form key={formKey} id={formId} onSubmit={handleSubmit}>
      <Flex
        align="center"
        justify="between"
        gap="3"
        wrap="wrap"
        className={cn(
          isWorkspaceSurface && "border-[color:var(--gray-a5)] border-b pb-2",
        )}
      >
        <Flex align="center" gap="2" wrap="wrap" className="min-w-0">
          {symbolSearch}
          <OrdersStatusTabs
            selectedStatusTab={selectedStatusTab}
            rowCount={rowCount}
            onStatusTabChange={onStatusTabChange}
          />
          <OrdersTimeWindowButton
            filters={filters}
            formId={formId}
            validationError={validationError}
            validationErrorId={validationErrorId}
            onFiltersChange={onFiltersChange}
          />
          {columnsPopover}
        </Flex>
        <Flex align="center" gap="2" wrap="nowrap">
          {lastUpdatedLabel ? (
            <Text size="1" color="gray" className="atd-numeric" wrap="nowrap">
              {lastUpdatedLabel}
            </Text>
          ) : null}
          <Flex align="center" gap="1" wrap="nowrap">
            <Button
              type="button"
              size="1"
              variant="soft"
              color="gray"
              onClick={onReset}
            >
              Reset filters
            </Button>
            <Button
              type="button"
              size="1"
              variant="soft"
              color="gray"
              aria-label="Refresh orders"
              disabled={isRefreshDisabled}
              onClick={onRefresh}
            >
              <ArrowClockwiseIcon
                aria-hidden
                size={14}
                className={cn(
                  isRefreshing && "animate-spin motion-reduce:animate-none",
                )}
              />
              Refresh
            </Button>
          </Flex>
        </Flex>
      </Flex>
      <OrdersValidationNotice
        validationError={validationError}
        validationErrorId={validationErrorId}
      />
    </form>
  );
};
